import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Form } from 'antd';
import { GeneralInformationNodeRenderer, BalanceInformationNodeRenderer } from '../../node_renderers';
import { PanelSwitch, ErrorsRenderer } from '../../../custom_shared/components';
import { AddBalanceInformationButton, WorksheetProcessingResultsRenderer } from '..';
import { WorksheetModel } from '../../models';
import { useAppStore } from '../../stores';
import { useWorksheetForm } from '../../hooks';
import WorksheetRendererHeader from './WorksheetRendererHeader';
import './WorksheetRenderer.less';

interface Props {
    worksheet: WorksheetModel;
}

const WorksheetRenderer: React.FC<Props> = ({ worksheet }) => {
    const { sessionStore } = useAppStore();

    const { form, formKey, onValuesChange } = useWorksheetForm(worksheet);

    const containerRef = React.useRef<HTMLDivElement>(null);

    const nodeRefs = React.useRef<Map<string, React.RefObject<HTMLDivElement>>>(new Map());

    const addNodeRef = React.useCallback((nodeId: string, nodeRef: React.RefObject<HTMLDivElement>) => {
        nodeRefs.current.set(nodeId, nodeRef);
    }, []);

    const showLoader = sessionStore.isLoading || sessionStore.inProgress;

    const containerClassName = React.useMemo(() => {
        let className = 'worksheet-renderer-container';

        if (worksheet.isSelected) {
            className += ' active';
        } else {
            className += ' hidden';
        }

        return className;
    }, [worksheet.isSelected]);

    const nodesContainerClassName = React.useMemo(() => {
        let className = 'worksheet-nodes-container custom-scroll';

        if (showLoader) {
            className += ' loading';
        }

        if (worksheet.inProgress) {
            className += ' in-progress';
        }

        return className;
    }, [showLoader, worksheet.inProgress]);

    return (
        <div ref={containerRef} className={containerClassName}>
            <WorksheetRendererHeader worksheet={worksheet} />
            <WorksheetProcessingResultsRenderer worksheet={worksheet} />

            {worksheet.errors.length > 0 && <ErrorsRenderer errors={worksheet.errors} />}

            <PanelSwitch
                panelsRefs={nodeRefs}
                containerRef={containerRef}
                options={worksheet.nodes.map(node => ({ id: node.id, label: node?.name ?? '' }))}
            />

            <Form id={worksheet.id} key={formKey} form={form} onValuesChange={onValuesChange}>
                <div className={nodesContainerClassName}>
                    {worksheet.generalInformationNode && (
                        <GeneralInformationNodeRenderer
                            node={worksheet.generalInformationNode}
                            addNodeRef={addNodeRef}
                        />
                    )}

                    {worksheet.balanceInformationNodes.map(node => (
                        <BalanceInformationNodeRenderer key={node.id} node={node} addNodeRef={addNodeRef} />
                    ))}

                    <AddBalanceInformationButton worksheet={worksheet} />
                </div>
            </Form>
        </div>
    );
};

export default observer(WorksheetRenderer);
